import { useState } from 'react';
import { isRotation, t, type CropCorners, type Rotation } from '@bms2/shared';
import { ScanConfirmScreen, type ScanConfirmState } from '../../components/files/ScanConfirmScreen';
import { Modal } from '../../components/modals';
import { Text } from '../../components/Text';
import { post, useApi, FILES_PATH } from './api';

/**
 * Scan confirm on the record page (piece 9; decision 0345): the review screen
 * over one held upload. The detected rotation and crop come from the API,
 * Confirm runs the pipeline and Retry re-runs detection on the same original;
 * neither is ever applied silently. The page reloads when the pipeline
 * completes or names the step that failed.
 */
export const scanConfirmPath = (fileId: string) => `${FILES_PATH}/${fileId}/scan-confirm`;

interface StateAnswer {
  confirm: { detectedRotation: number; detectedCropCorners: CropCorners | null; confirmedRotation: number | null; confirmedCropCorners: CropCorners | null };
  boundaryFound: boolean;
  photoPipelineStatus: string | null;
  blocksPipeline: boolean;
  imageSize: { width: number; height: number };
}

interface ConfirmAnswer {
  status: string;
  versionId: string | null;
  failedStep: string | null;
}

export interface ScanConfirmModalProps {
  open: boolean;
  fileId: string | null;
  filename: string | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** The pipeline finished: the message the page shows, and the reload that follows. */
  onComplete: (message: string) => void;
}

export function ScanConfirmModal({ open, fileId, filename, apiBaseUrl = '', onCancel, onComplete }: ScanConfirmModalProps) {
  const path = open && fileId ? scanConfirmPath(fileId) : null;
  const fetched = useApi<StateAnswer>(path, apiBaseUrl);
  /** A state the person's own action put the screen in; null reads the detection as it stands, so a Retry answered resets the screen. */
  const [override, setOverride] = useState<ScanConfirmState | null>(null);
  const [failedStep, setFailedStep] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const answer = fetched.state.kind === 'ready' ? fetched.state.data : null;
  const mode: ScanConfirmState | null = answer ? override ?? (answer.boundaryFound ? 'detected' : 'noBoundary') : null;
  const setMode = setOverride;

  const confirm = async (confirmation: { rotation: Rotation; corners: CropCorners }) => {
    if (!fileId) return;
    setMode('processing');
    const outcome = await post<ConfirmAnswer>(scanConfirmPath(fileId), { confirmedRotation: confirmation.rotation, confirmedCropCorners: confirmation.corners }, apiBaseUrl);
    if (!outcome.ok) {
      setMessage(outcome.message);
      setMode(null);
      return;
    }
    if (outcome.data.status === 'Complete') {
      onComplete(t('scan.complete'));
      return;
    }
    setFailedStep(outcome.data.failedStep);
    setMode('failed');
  };

  const retry = async () => {
    if (!fileId) return;
    const outcome = await post(`${scanConfirmPath(fileId)}/retry`, {}, apiBaseUrl);
    if (!outcome.ok) {
      setMessage(outcome.message);
      return;
    }
    setFailedStep(null);
    setOverride(null);
    fetched.reload();
  };

  const rotation: Rotation = answer && isRotation(answer.confirm.detectedRotation) ? answer.confirm.detectedRotation : 0;

  return (
    <Modal
      open={open}
      title={t('scan.modalTitle')}
      {...(filename ? { subtitle: filename } : {})}
      onCancel={onCancel}
      width="wide"
      dataModal="scan-confirm"
      actions={[
        // 🔒 Decision 2140: one Close where the window writes nothing (decision 3063).
        { kind: 'close', label: t('scan.close'), onAct: onCancel },
      ]}
    >
      <div data-scan-confirm-modal={fileId ?? ''}>
        <Text as="p" variant="caption">{t('scan.modalHint')}</Text>
        {message ? <Text as="p" variant="caption">{message}</Text> : null}
        {answer && mode && fileId ? (
          <ScanConfirmScreen
            imageUrl={`${apiBaseUrl}${FILES_PATH}/${fileId}/stream`}
            detectedRotation={rotation}
            detectedCropCorners={answer.confirm.detectedCropCorners}
            imageSize={answer.imageSize}
            state={mode}
            failedStep={failedStep}
            onConfirm={(confirmation) => void confirm(confirmation)}
            onRetry={() => void retry()}
            onAdjustManually={() => setMode('adjusting')}
          />
        ) : (
          <Text as="p" variant="caption">{fetched.state.kind === 'failed' ? fetched.state.error : t('scan.loading')}</Text>
        )}
      </div>
    </Modal>
  );
}
